import { useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'

import { Plus16 } from '@makyoapp/makyo-icon/icon'
import { Button } from '@makyoapp/makyo-ui'

import { useAddTeamMutation } from '@/resolvers/user/mutation'
import { useGetMyTeams } from '@/resolvers/user/query'
import { useReqError } from '@/hooks/useReqError'
import { WrapperProps } from '@/shared/routes'
import Layout from '@/components/Layout'
import { NewOrgTeamModalForm, NewOrgTeamModalSubmit } from '@/components/NewOrgTeamModalForm'
import { CardSkeletons } from '@/components/Skeletons'

import { ListTeamCard } from './components/ListTeamCard'

/**
 * MyTeams is a function component that renders the user's teams page.
 * It uses the useMyTeams custom hook to handle the teams.
 *
 *
 * @todo
 *
 * @route `u/my-orgs`
 */
export default function MyTeams(props: WrapperProps) {
  const { t } = useTranslation()
  const printAPIError = useReqError()

  // access my teams
  const { data: myTeams, isLoading, refetch } = useGetMyTeams()

  // access add team mutation
  const { mutate: addTeamMutation } = useAddTeamMutation()

  // local state
  const [showModal, setShowModal] = useBoolean(false)

  /**
   * Function to handle the submission of a new team.
   * Makes use of mutations to add a new team.
   * @param {NewOrgTeamModalSubmit} newTeam - The new team to add.
   * @param {() => void} done - Callback function to be called when the mutation is settled.
   * @returns {void} Returns nothing.
   */
  const handleSubmit = (newTeam: NewOrgTeamModalSubmit, done: () => void) =>
    addTeamMutation(
      { newTeam },
      {
        onSuccess() {
          handleAfterUpdate()
        },
        onError() {
          printAPIError
        },
        onSettled() {
          done?.()
          setShowModal(false)
        },
      }
    )

  /**
   * Function to handle updates.
   * It first sets the loading state to true, then refetches the data.
   * If the refetched data exists, it will update the organization's teams with the new data.
   * @returns {void} Returns nothing.
   */
  const handleAfterUpdate = () => {
    refetch()
  }

  return (
    <Layout
      title={props.title} // Setting the page title
      actions={
        // Setting the actions
        <>
          <Button // Button to add new team
            id={'new-team-btn'}
            icon={<Plus16 />}
            onClick={() => {
              setShowModal(true) // Open modal form on click
            }}
          >
            {t('Add New Team')}
          </Button>
          <NewOrgTeamModalForm // Modal form to add new team
            isTeam={true}
            formId='team-list-new-team'
            onSubmit={handleSubmit} // Handle form submit
            show={showModal} // Show modal form
            onHide={() => setShowModal(false)} // Hide modal form
          />
        </>
      }
    >
      {isLoading ? ( // If data is loading, show loading component
        <CardSkeletons total={6} />
      ) : (
        <div className='flex flex-col gap-12'>
          <ListTeamCard // List of user teams
            id='my-teams'
            teams={myTeams || []} // Teams data
            onAfterUpdate={handleAfterUpdate} // Handle update action
          />
        </div>
      )}
    </Layout>
  )
}
